import { Grid } from '@mui/material';
import { useContext, useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    Button,
    PageWrapper,
    Searchbar,
    Table,
    Typography,
    TableStatusFilter,
    FilterButton,
    Checkbox,
    TextField
} from '../../components/share';
import { useMutation } from '@tanstack/react-query';
import { alertDialogContext } from '../../context/AlertDialogProvider';
import { snackbarContext } from '../../context/SnackbarProvider';
import { userContext } from '../../context/UserProvider';
import { exportCSV, getPermission } from '../../utils'
import { STATUS } from './index'
import dayjs from 'dayjs';
import TableDateFilter from '../../components/share/TableDateFilter';
import { fetchRSPReport } from '../../apis/report';
import { manageStocks, getManageStocksPreview } from '../../apis/order';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogTitle from '@mui/material/DialogTitle';
import { Box, CircularProgress, Divider, Alert, Chip } from '@mui/material';
import constants from '../../constants';

const margin = 10

function OrderListPage({ columns, orderPermission = { canView: false, canModify: false }, reportPermission = { canView: false, canModify: false } }) {
    const navigate = useNavigate();
    const [snackbar, setSnackbar] = useContext(snackbarContext);
    const [dialog, setDialog] = useContext(alertDialogContext);
    const [user] = useContext(userContext);
    // const [selectMode, setSelectMode] = useState(false);

    const { canView: canViewOrders, canModify: canModifyOrders } = orderPermission;
    const { canView: canViewReport, canModify: canModifyReport } = reportPermission;
    
    // Check if user has write permission
    const hasOrderManageStockPermission = (user?.permissions?.orderManageStock || [] ).includes('w') || false;

    const [filter, setFilter] = useState({
        keyword: "",
        // status: null
    });

    const [refreshKey, setRefreshKey] = useState(0);
    const [isReloading, setIsReloading] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [stockDialog, setStockDialog] = useState({
        open: false,
        step: 1, // 1: initial prompt, 2: confirmation with input
        order: null,
        action: null, // 'release' or 'sold'
        confirmationInput: '',
        previewData: null,
        previewLoading: false,
        previewError: null
    });

    const onApiError = (error) => {
        setSnackbar({
            type: 'error',
            message: `${error?.response?.data?.result?.errorMessage?.en || error?.message}`
        })
    }

    const rspReportMutation = useMutation({
        mutationFn: (payload) => fetchRSPReport(payload),
        onSuccess: (data) => {
            setSnackbar({
                type: 'success',
                message: 'Success'
            })
            // TODO: confirm download file name
            exportCSV(data, `RSP_Report_${dayjs().format('DD-MM-YYYY')}`);
        },
        onError: (error) => {
            setSnackbar({
                type: 'error',
                message: `${error.code} ${error.errorMessage ? `- ${error.errorMessage?.en}` : ""}`
            })
        }
    })

    const manageStocksMutation = useMutation({
        mutationFn: ({ orderId, payload }) => manageStocks(orderId, payload),
        onSuccess: (data) => {
            setIsSubmitting(false);
            setStockDialog({
                open: false,
                step: 1,
                order: null,
                action: null,
                confirmationInput: '',
                previewData: null,
                previewLoading: false,
                previewError: null
            });
            setDialog({
                open: true,
                title: 'Success',
                message: `Stock has been ${stockDialog.action === 'release' ? 'released' : 'marked as sold'} successfully.`,
                actionText: 'OK'
            });
        },
        onError: (error) => {
            setIsSubmitting(false);
            setDialog({
                open: true,
                title: 'Error',
                message: `${error?.code || 'Error'} ${error?.errorMessage?.en || error?.message || 'Failed to manage stocks'}`,
                actionText: 'OK'
            });
        }
    })

    const previewMutation = useMutation({
        mutationFn: (orderId) => getManageStocksPreview(orderId),
        onSuccess: (response) => {
            setStockDialog(prev => ({
                ...prev,
                previewData: response?.data || response,
                previewLoading: false,
                previewError: null
            }));
        },
        onError: (error) => {
            setStockDialog(prev => ({
                ...prev,
                previewLoading: false,
                previewError: error?.errorMessage?.en || error?.message || 'Failed to load preview'
            }));
        }
    })

    const handleManageStock = (order) => {
        setStockDialog({
            open: true,
            step: 1,
            order: order,
            action: null,
            confirmationInput: '',
            previewData: null,
            previewLoading: false,
            previewError: null
        });
    }

    const handleStockAction = (action) => {
        setStockDialog(prev => ({
            ...prev,
            step: 2,
            action: action,
            confirmationInput: '',
            previewLoading: true,
            previewError: null
        }));
        
        // Fetch preview data
        previewMutation.mutate(stockDialog.order._id);
    }

    const handleConfirmAction = () => {
        if (isSubmitting || manageStocksMutation.isPending) return; // Prevent double submission
        
        const expectedText = stockDialog.action === 'release' ? 'Release' : 'Sold';
        if (stockDialog.confirmationInput === expectedText) {
            setIsSubmitting(true);
            manageStocksMutation.mutate({
                orderId: stockDialog.order._id,
                payload: { forceUpdateStock: stockDialog.action }
            });
        }
    }

    const handleCloseStockDialog = () => {
        if (isSubmitting || manageStocksMutation.isPending) return; // Prevent closing during submission
        
        setStockDialog({
            open: false,
            step: 1,
            order: null,
            action: null,
            confirmationInput: '',
            previewData: null,
            previewLoading: false,
            previewError: null
        });
        setIsSubmitting(false);
    }

    const isConfirmButtonEnabled = () => {
        const expectedText = stockDialog.action === 'release' ? 'Release' : 'Sold';
        const inputMatches = stockDialog.confirmationInput === expectedText;
        const canManage = stockDialog.previewData?.canManageStocks !== false;
        return inputMatches && canManage && !stockDialog.previewLoading;
    }

    const handleOnClickExport = async () => {
        // console.log('==onClick Export RSP Report with filter', filter);

        const payload = Object.keys(filter).reduce((acc, key) => {
            if (filter?.[key] && filter?.[key] !== "") { acc[key] = filter[key] }
            return acc
        }, {});

        // If no start date or end date, return error
        if (!payload?.startDateTime || !payload?.endDateTime) {
            return onApiError({ message: `INVALID_INPUT - Start Date and End Date are required` })
        }

        // Remove keyword key from payload
        if (payload?.keyword) { delete payload.keyword }

        rspReportMutation.mutate(payload);
    }

    // Update columns with handleManageStock function
    const updatedColumns = useMemo(() => {
        return columns.map(col => {
            if (col.field === 'stockAlert') {
                // Hide Stock Alert column if user doesn't have write permission
                if (!hasOrderManageStockPermission) {
                    return {
                        ...col,
                        hide: true
                    };
                }
                
                return {
                    ...col,
                    getActions: (params) => {
                        if (params.row.settledStock) {
                            return [
                                <span 
                                    style={{ color: 'green', fontWeight: 'bold', textDecoration: 'underline', cursor: 'pointer' }} 
                                    onClick={() => handleManageStock(params.row)}
                                >
                                    Settled
                                </span>,
                            ]
                        } else if (params.row.alertStock) {
                            return [
                                <span 
                                    style={{ color: 'red', fontWeight: 'bold', textDecoration: 'underline', cursor: 'pointer' }} 
                                    onClick={() => handleManageStock(params.row)}
                                >
                                    Manage
                                </span>,
                            ]
                        }
                        return []
                    }
                }
            }
            return col;
        });
    }, [columns, hasOrderManageStockPermission]);

    const Header = () => {
        return (
            <>
                <Typography bold style={{ flex: 1 }}>{`Orders`}</Typography>
                {/* {
                    canView &&
                    <FilterButton
                        style={{ marginRight: margin }}
                        filters={[
                            {
                                type: 'date-range',
                                startDataKey: 'startDate',
                                endDataKey: 'endDate',
                            },
                            {
                                type: 'select',
                                dataKey: 'status',
                                options: STATUS
                            }
                        ]}
                        disabled={!canView}
                        value={filter}
                        onChange={(newFilterValue) => setFilter({ ...filter, ...newFilterValue })}
                    />
                } */}
                { canViewReport && <Button color="orange" style={{ marginRight: margin }} onClick={handleOnClickExport} disabled={rspReportMutation.isLoading}>{rspReportMutation.isLoading ? 'Exporting ...' : 'Export'}</Button> }
                {/* <Button color="green" style={{ marginRight: margin }} onClick={handleOnOpenFilter}>Filter</Button> */}
            </>
        )
    }

    return (
        <>
            <PageWrapper
                renderHeader={Header}
                renderCardHeader={() => (<div />)}
            >
                <Grid container direction='row' alignItems='center' style={{ paddingRight: 10 }}>
                    <TableDateFilter type="date-range" filter={filter} setFilter={setFilter} fromId={"startDateTime"} toId={"endDateTime"} />
                    <TableStatusFilter width={200} options={STATUS} value={filter.status} disabled={!canViewOrders} onChange={(newValue) => setFilter({ ...filter, status: newValue })} />
                    <Searchbar value={filter.keyword} onChange={(e) => setFilter({ ...filter, keyword: e.target.value })} />
                    {/* <Checkbox label="Select" fullWidth={false} typographyProps={{ color: "primary" }} checked={selectMode} onChange={(event) => setSelectMode(event.target.checked)} /> */}
                </Grid>
                {isReloading ? (
                    <Box display="flex" justifyContent="center" alignItems="center" py={10}>
                        <CircularProgress />
                    </Box>
                ) : (
                    <Table
                        key={refreshKey}
                        style={{ margin: `0px 20px 20px 20px` }}
                        columns={updatedColumns}
                        api={"/order"}
                        apiParams={{
                            ...filter
                        }}
                        sortingMode="server"
                        onError={onApiError}
                        setSnackbar={setSnackbar}
                        onDataLoaded={() => setIsReloading(false)}
                        // onRowClick={(params) => {
                        //     navigate(`/terms-and-conditions/${params.id}`)
                        // }}
                        // checkboxSelection={selectMode}
                        // onSelectionModelChange={(item) => {
                        //     console.log('==onSelectionModelChange', item);
                        // }}
                    />
                )}
            </PageWrapper>

            {/* Stock Management Dialog */}
            <Dialog
                open={stockDialog.open}
                onClose={(event, reason) => {
                    if (reason === 'backdropClick' || reason === 'escapeKeyDown') {
                        return; // Prevent closing on backdrop click or ESC key
                    }
                    handleCloseStockDialog();
                }}
                maxWidth="md"
                fullWidth
            >
                <DialogTitle>
                    {stockDialog.step === 1 ? 'Manage Stock' : 'Confirm Action'}
                </DialogTitle>
                <DialogContent sx={{ maxHeight: stockDialog.step === 2 ? '70vh' : 'auto', overflowY: stockDialog.step === 2 ? 'auto' : 'visible' }}>
                    {(manageStocksMutation.isPending || isSubmitting) && (
                        <Box 
                            sx={{ 
                                position: 'absolute', 
                                top: 0, 
                                left: 0, 
                                right: 0, 
                                bottom: 0, 
                                display: 'flex', 
                                justifyContent: 'center', 
                                alignItems: 'center', 
                                bgcolor: 'rgba(255, 255, 255, 0.8)', 
                                zIndex: 1000 
                            }}
                        >
                            <Box textAlign="center">
                                <CircularProgress />
                                <Typography variant="body2" sx={{ mt: 2 }}>
                                    Processing {stockDialog.action === 'release' ? 'Release' : 'Sold'}...
                                </Typography>
                            </Box>
                        </Box>
                    )}
                    {stockDialog.step === 1 ? (
                        <>
                            <DialogContentText>
                                <strong>Order ID:</strong> {stockDialog.order?._id}
                            </DialogContentText>
                            <DialogContentText>
                                <strong>Order Number:</strong> {stockDialog.order?.orderNumber}
                            </DialogContentText>
                            <DialogContentText style={{ marginTop: 16 }}>
                                Please select an action to manage the stocks manually for this order.
                            </DialogContentText>
                        </>
                    ) : (
                        <>
                            {stockDialog.previewLoading ? (
                                <Box display="flex" justifyContent="center" alignItems="center" py={4}>
                                    <CircularProgress />
                                </Box>
                            ) : stockDialog.previewError ? (
                                <Alert severity="error" sx={{ mb: 2 }}>
                                    {stockDialog.previewError}
                                </Alert>
                            ) : stockDialog.previewData ? (
                                <>
                                    {/* Order Information */}
                                    <Box mb={2}>
                                        <Typography variant="subtitle2" fontWeight="bold" gutterBottom>
                                            Order Information
                                        </Typography>
                                        <Typography variant="body2">
                                            <strong>Order ID:</strong> {stockDialog.previewData.orderId}
                                        </Typography>
                                        <Typography variant="body2">
                                            <strong>Order Number:</strong> {stockDialog.previewData.orderNumber}
                                        </Typography>
                                        <Typography variant="body2">
                                            <strong>Order Status:</strong> {stockDialog.previewData.orderStatus}
                                        </Typography>
                                    </Box>

                                    <Divider sx={{ my: 2 }} />

                                    {/* Settlement Information */}
                                    {stockDialog.previewData.settlementInfo?.isSettled && (
                                        <>
                                            <Box mb={2}>
                                                <Alert severity="info" sx={{ mb: 1 }}>
                                                    <Typography variant="subtitle2" fontWeight="bold" gutterBottom>
                                                        Settlement Information
                                                    </Typography>
                                                    <Typography variant="body2">
                                                        <strong>Status:</strong> Already Settled
                                                    </Typography>
                                                    <Typography variant="body2">
                                                        <strong>Action:</strong> {stockDialog.previewData.settlementInfo.settledAction?.toUpperCase() || 'N/A'}
                                                    </Typography>
                                                    <Typography variant="body2">
                                                        <strong>Settled By:</strong> {stockDialog.previewData.settlementInfo.settledByAdmin?.name || 'N/A'}
                                                    </Typography>
                                                    {stockDialog.previewData.settlementInfo.settledByAdmin?.email && (
                                                        <Typography variant="body2">
                                                            <strong>Email:</strong> {stockDialog.previewData.settlementInfo.settledByAdmin.email}
                                                        </Typography>
                                                    )}
                                                    {stockDialog.previewData.settlementInfo.settledByAdmin?.corphqId && (
                                                        <Typography variant="body2">
                                                            <strong>Corp HQ ID:</strong> {stockDialog.previewData.settlementInfo.settledByAdmin.corphqId}
                                                        </Typography>
                                                    )}
                                                    <Typography variant="body2">
                                                        <strong>Settled At:</strong> {stockDialog.previewData.settlementInfo.settledAt 
                                                            ? dayjs(stockDialog.previewData.settlementInfo.settledAt).format(constants.DATE_TIME_FORMAT)
                                                            : 'N/A'}
                                                    </Typography>
                                                </Alert>
                                            </Box>
                                            <Divider sx={{ my: 2 }} />
                                        </>
                                    )}

                                    {/* Cannot Manage Reason */}
                                    {!stockDialog.previewData.canManageStocks && stockDialog.previewData.cannotManageReason && (
                                        <>
                                            <Alert severity="warning" sx={{ mb: 2 }}>
                                                <Typography variant="body2">
                                                    <strong>Cannot Manage:</strong> {stockDialog.previewData.cannotManageReason}
                                                </Typography>
                                            </Alert>
                                            <Divider sx={{ my: 2 }} />
                                        </>
                                    )}

                                    {/* Affected Stocks */}
                                    <Box mb={2}>
                                        <Typography variant="subtitle2" fontWeight="bold" gutterBottom>
                                            Affected Stocks Summary
                                        </Typography>
                                        <Typography variant="body2" gutterBottom>
                                            <strong>Total Count:</strong> {stockDialog.previewData.affectedStocks?.totalCount || 0}
                                        </Typography>
                                        <Typography variant="body2" gutterBottom>
                                            <strong>On-Hold Stock Count:</strong> {stockDialog.previewData.affectedStocks?.onHeldStockCount || 0}
                                        </Typography>
                                        <Typography variant="body2" gutterBottom>
                                            <strong>On-Hold Number Count:</strong> {stockDialog.previewData.affectedStocks?.onHoldNumberCount || 0}
                                        </Typography>
                                        
                                        {stockDialog.previewData.affectedStocks?.products?.length > 0 && (
                                            <Box mt={3}>
                                                <Typography variant="body2" fontWeight="bold" gutterBottom>
                                                    Products:
                                                </Typography>
                                                {stockDialog.previewData.affectedStocks.products.map((product, index) => (
                                                    <Box key={index} sx={{ ml: 2, mb: 2, p: 1.5, bgcolor: 'grey.50', borderRadius: 1 }}>
                                                        <Typography variant="body2">
                                                            • <strong>{product.productName?.en || 'N/A'}</strong>
                                                            {product.productVariationName?.en && ` - ${product.productVariationName.en}`}
                                                        </Typography>
                                                        <Typography variant="body2" sx={{ ml: 2, fontSize: '0.875rem', color: 'text.secondary' }}>
                                                            Internal Code: {product.internalCode || 'N/A'}
                                                        </Typography>
                                                        <Typography variant="body2" sx={{ ml: 2, fontSize: '0.875rem', color: 'text.secondary' }}>
                                                            Total Stocks: {product.totalStockCount || 0} | 
                                                            <span style={{ color: product.willBeUpdatedCount > 0 ? '#2e7d32' : 'inherit', fontWeight: product.willBeUpdatedCount > 0 ? 'bold' : 'normal' }}>
                                                                {' '}Will Be Updated: {product.willBeUpdatedCount || 0}
                                                            </span>
                                                        </Typography>
                                                        
                                                        {product.stocks && product.stocks.length > 0 && (
                                                            <Box sx={{ ml: 2, mt: 1 }}>
                                                                <Typography variant="caption" color="text.secondary" display="block" gutterBottom>
                                                                    Stock Details:
                                                                </Typography>
                                                                {product.stocks.map((stock, stockIdx) => (
                                                                    <Box 
                                                                        key={stockIdx} 
                                                                        sx={{ 
                                                                            ml: 1, 
                                                                            mb: 0.5, 
                                                                            p: 1,
                                                                            display: 'flex', 
                                                                            alignItems: 'center', 
                                                                            gap: 1,
                                                                            bgcolor: stock.willBeUpdated ? '#e8f5e9' : '#fff9c4',
                                                                            borderRadius: 0.5,
                                                                            border: stock.willBeUpdated ? '1px solid #4caf50' : '1px solid #fdd835',
                                                                            flexWrap: 'wrap'
                                                                        }}
                                                                    >
                                                                        <Typography 
                                                                            variant="caption" 
                                                                            sx={{ 
                                                                                fontSize: '0.75rem', 
                                                                                fontFamily: 'monospace',
                                                                                fontWeight: 'bold',
                                                                                color: 'text.primary'
                                                                            }}
                                                                        >
                                                                            {stock.stockId || 'N/A'}
                                                                        </Typography>
                                                                        <Typography variant="caption" sx={{ fontSize: '0.7rem', color: 'text.secondary' }}>
                                                                            -
                                                                        </Typography>
                                                                        <Chip 
                                                                            label={stock.currentStatus || 'UNKNOWN'} 
                                                                            size="small" 
                                                                            sx={{ height: 20, fontSize: '0.65rem' }}
                                                                            color={stock.currentStatus === 'ON_HELD' ? 'warning' : 'default'}
                                                                        />
                                                                        {stock.willBeUpdated ? (
                                                                            <>
                                                                                <Typography variant="caption" sx={{ fontSize: '0.7rem', fontWeight: 'bold', color: '#2e7d32' }}>
                                                                                    →
                                                                                </Typography>
                                                                                <Chip 
                                                                                    label={stockDialog.action === 'release' ? 'AVAILABLE' : 'SOLD'} 
                                                                                    size="small" 
                                                                                    color="success"
                                                                                    sx={{ height: 20, fontSize: '0.65rem', fontWeight: 'bold' }}
                                                                                />
                                                                            </>
                                                                        ) : (
                                                                            <Chip 
                                                                                label="No Change" 
                                                                                size="small" 
                                                                                sx={{ height: 20, fontSize: '0.65rem', bgcolor: '#fdd835', color: '#000' }}
                                                                            />
                                                                        )}
                                                                    </Box>
                                                                ))}
                                                            </Box>
                                                        )}
                                                    </Box>
                                                ))}
                                            </Box>
                                        )}

                                        {stockDialog.previewData.affectedStocks?.plans?.length > 0 && (
                                            <Box mt={3}>
                                                <Typography variant="body2" fontWeight="bold" gutterBottom>
                                                    Plans:
                                                </Typography>
                                                {stockDialog.previewData.affectedStocks.plans.map((plan, planIndex) => (
                                                    <Box key={planIndex} sx={{ ml: 2, mb: 2, p: 1.5, bgcolor: 'grey.50', borderRadius: 1 }}>
                                                        <Typography variant="body2">
                                                            • <strong>{plan.planName?.en || 'N/A'}</strong>
                                                        </Typography>
                                                        <Typography variant="body2" sx={{ ml: 2, fontSize: '0.875rem', color: 'text.secondary' }}>
                                                            Total Numbers: {plan.totalNumberCount || 0} | Will be updated: {plan.willBeUpdatedCount || 0}
                                                        </Typography>
                                                        
                                                        {plan.numbers?.length > 0 && (
                                                            <Box sx={{ ml: 2, mt: 1 }}>
                                                                <Typography variant="caption" color="text.secondary" display="block" gutterBottom>
                                                                    Number Pool Status Details:
                                                                </Typography>
                                                                {plan.numbers.map((number, numIndex) => (
                                                                    <Box 
                                                                        key={numIndex}
                                                                        sx={{ 
                                                                            ml: 1, 
                                                                            p: 1,
                                                                            mb: 0.5,
                                                                            display: 'flex', 
                                                                            alignItems: 'center', 
                                                                            gap: 1,
                                                                            bgcolor: number.willBeUpdated ? '#e8f5e9' : '#fff9c4',
                                                                            borderRadius: 0.5,
                                                                            border: number.willBeUpdated ? '1px solid #4caf50' : '1px solid #fdd835',
                                                                            width: 'fit-content'
                                                                        }}
                                                                    >
                                                                        <Typography variant="caption" sx={{ fontSize: '0.75rem', fontWeight: 'bold' }}>
                                                                            {number.number}
                                                                        </Typography>
                                                                        <Typography variant="caption" sx={{ fontSize: '0.7rem', color: 'text.secondary' }}>
                                                                            -
                                                                        </Typography>
                                                                        <Typography variant="caption" sx={{ fontSize: '0.75rem', fontWeight: 'bold' }}>
                                                                            From:
                                                                        </Typography>
                                                                        <Chip 
                                                                            label={number.currentStatus?.toUpperCase() || 'UNKNOWN'} 
                                                                            size="small" 
                                                                            sx={{ height: 20, fontSize: '0.65rem' }}
                                                                            color={number.currentStatus === 'available' ? 'success' : number.currentStatus === 'on-hold' ? 'warning' : 'default'}
                                                                        />
                                                                        {number.willBeUpdated ? (
                                                                            <>
                                                                                <Typography variant="caption" sx={{ fontSize: '0.7rem', fontWeight: 'bold', color: '#2e7d32' }}>
                                                                                    →
                                                                                </Typography>
                                                                                <Typography variant="caption" sx={{ fontSize: '0.75rem', fontWeight: 'bold' }}>
                                                                                    To:
                                                                                </Typography>
                                                                                <Chip 
                                                                                    label={stockDialog.action === 'release' ? 'AVAILABLE' : 'SOLD'} 
                                                                                    size="small" 
                                                                                    color="success"
                                                                                    sx={{ height: 20, fontSize: '0.65rem', fontWeight: 'bold' }}
                                                                                />
                                                                            </>
                                                                        ) : (
                                                                            <>
                                                                                <Typography variant="caption" sx={{ fontSize: '0.7rem', fontWeight: 'bold', mx: 1 }}>
                                                                                    →
                                                                                </Typography>
                                                                                <Chip 
                                                                                    label="No Change" 
                                                                                    size="small" 
                                                                                    sx={{ height: 20, fontSize: '0.65rem', bgcolor: '#fdd835', color: '#000' }}
                                                                                />
                                                                            </>
                                                                        )}
                                                                    </Box>
                                                                ))}
                                                            </Box>
                                                        )}
                                                    </Box>
                                                ))}
                                            </Box>
                                        )}
                                    </Box>

                                    <Divider sx={{ my: 2 }} />

                                    {/* Confirmation Input */}
                                    <Box mt={2}>
                                        <DialogContentText>
                                            Please type <strong>{stockDialog.action === 'release' ? 'Release' : 'Sold'}</strong> to confirm your action.
                                        </DialogContentText>
                                        <TextField
                                            autoFocus
                                            margin="dense"
                                            fullWidth
                                            value={stockDialog.confirmationInput}
                                            onChange={(e) => setStockDialog(prev => ({ ...prev, confirmationInput: e.target.value }))}
                                            placeholder={stockDialog.action === 'release' ? 'Release' : 'Sold'}
                                            disabled={!stockDialog.previewData.canManageStocks || manageStocksMutation.isPending || isSubmitting}
                                        />
                                    </Box>
                                </>
                            ) : null}
                        </>
                    )}
                </DialogContent>
                <DialogActions>
                    {stockDialog.step === 1 ? (
                        <>
                            <Button onClick={handleCloseStockDialog}>Cancel</Button>
                            <Button onClick={() => handleStockAction('release')} color="primary">
                                Preview Release
                            </Button>
                            <Button onClick={() => handleStockAction('sold')} color="primary">
                                Preview Sold
                            </Button>
                        </>
                    ) : (
                        <>
                            <Button onClick={handleCloseStockDialog} disabled={manageStocksMutation.isPending || isSubmitting}>Cancel</Button>
                            <Button 
                                onClick={handleConfirmAction} 
                                color="primary"
                                disabled={!isConfirmButtonEnabled() || manageStocksMutation.isPending || isSubmitting}
                            >
                                {(manageStocksMutation.isPending || isSubmitting) ? (
                                    <>
                                        <CircularProgress size={16} sx={{ mr: 1 }} />
                                        Processing...
                                    </>
                                ) : (
                                    stockDialog.action === 'release' ? 'Release' : 'Sold'
                                )}
                            </Button>
                        </>
                    )}
                </DialogActions>
            </Dialog>
        </>
    )
}

export default OrderListPage